import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Pagination } from "components/Pagination";
import EllipsisText from "components/Ellipsis";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import { useNavigate } from "react-router-dom";
import Div from "@jumbo/shared/Div";
import { BASE_PATHS } from "app/utils/constants/routeConstants";
import JumboChipsGroup from "@jumbo/components/JumboChipsGroup";
import withPopoverChips from "components/withPopoverChips";
import StyledTableCell from "components/common/StyledTableCell";
import { colorForBgColor } from "@jumbo/utils";
import ListActionButtons from "components/ActionButtons/ListActionButtons";
import { USER_ROLES } from "app/utils/constants/constants";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { purple, pink } from "@mui/material/colors";

const EnhancedJumboChipsGroup = withPopoverChips(JumboChipsGroup);

const columns = (showActions) => [
  ...(showActions ? [{ id: "id", label: "ID", width: "10%" }] : []),
  { id: "title", label: "Title", width: showActions ? "60%" : "40%" },
  { id: "tags", label: "Tags", width: "30%" },
  ...(showActions ? [{ id: "actions", label: "", width: "20%" }] : []),
];

const ProblemTable = ({
  isAdmin,
  displayData,
  displayCount,
  loading,
  error,
  rows,
  currentPage,
  order,
  orderBy,
  detailRouteBase,
  onPageChange,
  onRequestSort,
  onView,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { authUser } = useJumboAuth();
  const [hoveredRow, setHoveredRow] = useState(null);
  const isPremiumUser =
    authUser?.authorities?.includes(USER_ROLES.ROLE_PREMIUM_USER) || false;

  return (
    <>
      <TableContainer sx={{ flexGrow: 1 }}>
        {loading ? (
          <Div
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              minWidth: 0,
              alignContent: "center",
              height: "100vh",
            }}
          >
            <CircularProgress sx={{ m: "-40px auto 0" }} />
            <Typography variant="body1" color="text.secondary">
              Loading problems...
            </Typography>
          </Div>
        ) : error ? (
          <Div sx={{ textAlign: "center", p: 3 }}>Error: {error}</Div>
        ) : !displayData || displayData.length === 0 ? (
          <JumboListNoDataPlaceholder text="No problems found" />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {columns(isAdmin, isPremiumUser).map((column) => (
                  <StyledTableCell
                    key={column.id}
                    style={{ width: column.width }}
                    onClick={
                      column.id !== "actions"
                        ? () => onRequestSort(column.id)
                        : undefined
                    }
                  >
                    {column.id !== "actions" ? (
                      <TableSortLabel
                        active={
                          orderBy ===
                          (column.id === "title"
                            ? "defaultTitle"
                            : column.id === "tags"
                            ? "problemTags"
                            : column.id)
                        }
                        direction={order}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((row) => {
                const titleValue = row.title || row.defaultTitle || "";
                const freeIcon =
                  row.forPaidOnly === false && !isPremiumUser ? (
                    <Tooltip title="Free to execute" arrow placement="top">
                      <LockOpenIcon
                        sx={{
                          fontSize: "16px",
                          color: pink["A400"],
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  ) : null;
                const videoIcon = row.hasVideos ? (
                  <Tooltip
                    title="Video explanation available"
                    arrow
                    placement="top"
                  >
                    <PlayArrowIcon
                      sx={{
                        fontSize: "16px",
                        verticalAlign: "middle",
                        color: pink[500],
                      }}
                    />
                  </Tooltip>
                ) : null;
                const displayTitle =
                  typeof titleValue === "object" && titleValue !== null
                    ? titleValue.name || ""
                    : titleValue;

                const rawTags = row.problemTags || row.tags || [];
                const tagArray = Array.isArray(rawTags)
                  ? rawTags.map((tag) =>
                      typeof tag === "object" && tag !== null
                        ? tag.name || ""
                        : tag
                    )
                  : typeof rawTags === "string"
                  ? rawTags.split(", ").filter(Boolean)
                  : [];

                const isAdminContext =
                  detailRouteBase === BASE_PATHS.ADMIN_PROBLEMS;

                return (
                  <TableRow
                    hover
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: `0 3px 1px 0 ${alpha("#000", 0.1)}`,
                        borderBottomColor: "transparent",
                      },
                    }}
                    onClick={() =>
                      navigate(
                        `${detailRouteBase}/${
                          isAdminContext ? row.id : row.name
                        }`
                      )
                    }
                  >
                    {isAdmin && <TableCell>{row.id}</TableCell>}
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EllipsisText
                          text={displayTitle}
                          maxLength={80}
                          tooltipPlacement="top"
                        />
                        {freeIcon}
                        {videoIcon}
                      </Box>
                    </TableCell>

                    <TableCell
                      onMouseEnter={(e) => e.stopPropagation()}
                      onMouseLeave={(e) => e.stopPropagation()}
                    >
                      {tagArray.length > 0 ? (
                        <EnhancedJumboChipsGroup
                          chips={tagArray.map((tag) => ({ name: tag }))}
                          mapKeys={{ label: "name" }}
                          spacing={1}
                          size="small"
                          max={3}
                          sx={{
                            bgcolor: purple[400],
                            color: colorForBgColor("primary"),
                            marginLeft: "4px",
                            "&:hover": {
                              bgcolor: purple[300],
                              color: colorForBgColor("primary"),
                            },
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No tags
                        </Typography>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <ListActionButtons
                          row={row}
                          onView={onView}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          showEdit={true}
                          showDelete={true}
                          isHovered={hoveredRow === row.id}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!loading && displayData && displayData.length > 0 && (
        <Pagination
          totalCount={displayCount}
          rowsPerPage={rows}
          page={currentPage}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};

export default ProblemTable;
